import { Component, EventEmitter, Input, Output, signal, OnInit, OnDestroy, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EdeskService, CreateDraftRequest, AttachmentDto, SignAndSendRequest, SignAndSendResponse, RecipientDto } from '../../core/edesk.service';
import { SendGeneralAgendaRequest } from '../../api-draft';
import { Subject, debounceTime, distinctUntilChanged, Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SignNotificationService, SignJobUpdate } from '../../core/sign-notification.service';

export interface ComposePayload {
  recipientId: string;
  subject: string;
  body: string;
}

type AttachmentVM = {
  file: File;
  base64?: string;
  error?: string;
  sign: boolean;    // whether the file is selected for signing
  signed?: boolean; // whether the file was signed (result badge)
};

/** One signed document offered in the remove-signature dialog */
type UnsignOption = {
  name: string;     // document name sent to the backend (form = GeneralAgenda.xml)
  label: string;    // label shown to the user
  isForm: boolean;
  selected: boolean;
};

@Component({
  selector: 'app-compose-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: `./compose-dialog.component.html`,
  styleUrls: ['./compose-dialog.component.scss'],
})
export class ComposeDialogComponent implements OnInit, OnDestroy {
  @Input() open = false;
  @Input() login: string = '9811026148';
  @Input() recipients: RecipientDto[] = [];
  @Input() autoCloseOnSuccess = false;

  @Output() sent = new EventEmitter<SendGeneralAgendaRequest>();
  @Output() submitMessage = new EventEmitter<ComposePayload>();
  @Output() close = new EventEmitter<void>();

  recipientId = '';
  subject = '';
  body = '';
  attachments: AttachmentVM[] = [];
  showErrorDialog = false;
  showConfirmDialog = false;
  touched = false;
  sending = signal(false);
  error = signal<string | null>(null);
  confirmMessage = signal<string>('');

  draftId: number | null = null;
  private draftChangeSubject = new Subject<void>();
  savingDraft = signal(false);
  private draftSubscription?: Subscription;
  private pendingSavePromise: Promise<void> | null = null;
  deeplink: string | null = null;
  currentJobId: string | null = null;
  signingInProgress = signal(false);
  signedSuccessfully = signal(false);

  /** Whether the auto-generated GeneralAgenda form is selected for signing */
  signForm = true;
  /** Whether the form was signed (result badge) */
  formSigned = false;
  /** Selection snapshot taken when the signing job was created */
  private pendingSignSelection: { form: boolean; names: string[] } | null = null;

  // Testing utilities (panel hidden, console logs remain)
  showTestPanel = signal(false);
  wsConnected = signal(false);

  private signNotificationService = inject(SignNotificationService);
  private wsSubscription?: Subscription;
  private visibilityChangeHandler?: () => void;

  @ViewChild('deeplinkAnchor') deeplinkAnchor?: ElementRef<HTMLAnchorElement>;

  constructor(private api: EdeskService) {}

  ngOnInit(): void {
    this.draftSubscription = this.draftChangeSubject
      .pipe(debounceTime(10000), distinctUntilChanged())
      .subscribe(() => this.saveDraft());

    // Monitor WebSocket connection status for test panel
    this.signNotificationService.connectionStatus$.subscribe(connected => {
      this.wsConnected.set(connected);
    });

    // Setup visibility handler for mobile deeplink navigation
    this.setupVisibilityHandler();
  }

  ngOnDestroy(): void {
    this.draftSubscription?.unsubscribe();
    this.wsSubscription?.unsubscribe();
    
    if (this.currentJobId) {
      this.signNotificationService.unsubscribeFromJob(this.currentJobId);
    }

    // Remove visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  onBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      // Trigger the same cancel flow to save draft before closing
      this.onCancel();
    }
  }

  onFileInputChange(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const filesArray = Array.from(input.files);
      this.addFiles(filesArray);
      // Reset input value so same file can be selected again
      input.value = '';
    }
  }

  onDragOver(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
  }

  onDrop(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopPropagation();
    if (evt.dataTransfer?.files && evt.dataTransfer.files.length > 0) {
      const filesArray = Array.from(evt.dataTransfer.files);
      this.addFiles(filesArray);
    }
  }

  removeAttachment(index: number): void { 
    this.attachments.splice(index, 1);
    this.saveDraftImmediately();
  }

  private async addFiles(files: File[]): Promise<void> {
    for (const file of files) {
      // Check if file already exists
      const exists = this.attachments.some(a => a.file.name === file.name && a.file.size === file.size);
      if (exists) {
        continue; // Skip duplicates
      }

      const attachment: AttachmentVM = { file, sign: true };
      this.attachments.push(attachment);

      // Read file as base64 in background
      try {
        attachment.base64 = await this.readFileAsBase64(file);
      } catch (err) {
        attachment.error = 'Nepodarilo sa načítať súbor.';
        console.error('Failed to read file:', file.name, err);
      }
    }
    
    this.saveDraftImmediately();
  }
  private readFileAsBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          // Remove the data URL prefix if present
          const base64 = result.split(',')[1] || result;
          resolve(base64);
        } else {
          reject(new Error('Failed to read file as base64.'));
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }
  private escapeHtml(s: string): string {
    return s.replace(/[&<>"'`=\/]/g, function (c) {
      return ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
        '`': '&#96;',
        '=': '&#61;',
        '/': '&#47;',
      } as { [key: string]: string })[c] || c;
    });
  }
  private toHtmlFromTextarea(s: string): string {
    // Simple implementation: escape HTML and replace newlines with <br>
    return this.escapeHtml(s).replace(/\n/g, '<br>');
  }
  
  resetForm(): void {
    if (this.currentJobId) {
      this.signNotificationService.unsubscribeFromJob(this.currentJobId);
    }
    this.wsSubscription?.unsubscribe();
    
    this.recipientId = '';
    this.subject = '';
    this.body = '';
    this.attachments = [];
    this.touched = false;
    this.error.set(null);
    this.draftId = null;
    this.deeplink = null;
    this.currentJobId = null;
    this.signingInProgress.set(false);
    this.signedSuccessfully.set(false);
    this.signForm = true;
    this.formSigned = false;
    this.pendingSignSelection = null;
    this.showErrorDialog = false;
    this.showSuccessDialog = false;
    this.showConfirmDialog = false;
    this.showCancelConfirmDialog = false;
    this.showDeleteConfirmDialog = false;
    this.showRemoveSignatureDialog = false;
    this.success.set(null);
  }

  // --- Signing selection helpers ---

  /** At least one document (form or attachment) is selected for signing */
  get hasSignSelection(): boolean {
    return this.signForm || this.attachments.some(a => a.sign && !!a.base64);
  }

  /** Every attachment is selected for signing (master checkbox state) */
  get allSignSelected(): boolean {
    return this.attachments.length > 0 && this.attachments.every(a => a.sign);
  }

  /** At least one attachment is selected (drives the master checkbox indeterminate state) */
  get someSignSelected(): boolean {
    return this.attachments.some(a => a.sign);
  }

  /** Selection is locked once a signing job exists (prepared, in progress, or completed) */
  get signSelectionLocked(): boolean {
    return this.sending() || this.signingInProgress() || this.signedSuccessfully() || !!this.currentJobId;
  }

  /** At least one document (form or attachment) currently carries a signature */
  get anySigned(): boolean {
    return this.formSigned || this.attachments.some(a => a.signed);
  }

  /** Master checkbox: select/deselect all attachments at once (the form has its own checkbox) */
  toggleSignAll(checked: boolean): void {
    for (const a of this.attachments) {
      a.sign = checked;
    }
  }

  /** Marks the documents of the completed signing job with the signed badge */
  private markSelectedAsSigned(): void {
    const selection = this.pendingSignSelection;
    // Merge with existing badges - a later job must not clear signatures from an earlier one
    this.formSigned = this.formSigned || (selection ? selection.form : this.signForm);
    for (const a of this.attachments) {
      if (selection ? selection.names.includes(a.file.name) : (a.sign && !!a.base64)) {
        a.signed = true;
      }
    }
    this.pendingSignSelection = null;
  }

  /**
   * Loads an existing draft into the compose form for editing.
   * Converts base64 attachment content back to File objects.
   */
  async loadDraft(draftId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.loadDraft(draftId));
      
      // Populate form fields
      this.draftId = response.draftId;
      this.subject = response.subject || '';
      this.body = response.content || '';
      
      // Set recipient only if it exists in the recipients list
      const recipientUri = response.recipientEdeskUri || '';
      const recipientExists = this.recipients.some(r => r.id === recipientUri);
      this.recipientId = recipientExists ? recipientUri : '';
      
      // Convert attachments from base64 to File objects
      this.attachments = [];
      
      if (response.attachments && response.attachments.length > 0) {
        for (const att of response.attachments) {
          try {
            // Decode base64 to bytes
            const base64 = att.unsignedContent || '';
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            
            // Create File object
            const file = new File(
              [byteArray], 
              att.unsignedDocumentName || 'file', 
              { type: att.unsignedMimetype || 'application/octet-stream' }
            );
            
            // Add to attachments list with base64 already available.
            // Only documents without a signature are preselected for signing;
            // the signed badge is restored from the backend.
            this.attachments.push({
              file: file,
              base64: base64,
              sign: att.isSigned !== true,
              signed: att.isSigned === true
            });
          } catch (err) {
            console.error('Failed to convert attachment:', att.unsignedDocumentName, err);
          }
        }
      }
      
      // Store job ID and signing status if available
      if (response.jobId) {
        this.currentJobId = response.jobId;
      } else {
        this.currentJobId = null;
      }
      
      // Update signing state based on loaded draft
      this.signingInProgress.set(false);
      this.formSigned = response.formSigned === true;
      this.signForm = !this.formSigned;

      // Fully signed draft -> locked "signed" state. Partially signed draft
      // (e.g. after removing only some signatures) -> unlocked, so the
      // remaining documents can be signed in a new signing round.
      const allDocsSigned = this.formSigned && this.attachments.every(a => a.signed);
      if (response.isSigned && allDocsSigned) {
        this.signedSuccessfully.set(true);
      } else {
        this.signedSuccessfully.set(false);
        if (response.isSigned) {
          // The stored job belongs to a previous round; drop it so "Prepare for
          // signing" is available again (backend replaces the job on the next sign)
          this.currentJobId = null;
        }
      }
      
      console.log('✅ Draft loaded successfully:', draftId, {
        subject: this.subject,
        attachmentCount: this.attachments.length,
        isSigned: response.isSigned,
        jobId: response.jobId
      });
      
    } catch (err) {
      console.error('❌ Failed to load draft:', err);
      const errorMsg = (err as any)?.error?.message || (err as any)?.message || 'Nepodarilo sa načítať koncept.';
      this.showError(errorMsg);
    }
  }

  onCancel(): void {
    // Only show confirmation if user has actually started working (draft saved or content exists)
    const hasContent = this.recipientId || this.subject.trim() || this.body.trim() || this.attachments.length > 0;
    const hasDraft = this.draftId !== null;
    
    if (hasDraft || hasContent) {
      this.showCancelConfirmDialog = true;
    } else {
      // Nothing to lose, just close directly
      this.resetForm();
      this.close.emit();
    }
  }

  async confirmCancel(): Promise<void> {
    this.showCancelConfirmDialog = false;
    
    // Save the draft before closing if there's content
    const hasContent = this.recipientId && this.subject.trim();
    if (hasContent) {
      try {
        await this.ensureDraftSaved();
        console.log('✓ Draft saved before closing');
      } catch (err) {
        console.error('Failed to save draft before closing:', err);
        // Continue with closing even if save fails
      }
    }
    
    // Reset the form and close
    this.resetForm();
    this.close.emit();
  }

  dismissCancelConfirm(): void {
    this.showCancelConfirmDialog = false;
  }

  onCancelConfirmBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.dismissCancelConfirm();
    }
  }

  onDeleteDraft(): void {
    if (this.draftId === null) {
      return;
    }
    this.showDeleteConfirmDialog = true;
  }

  async confirmDeleteDraft(): Promise<void> {
    if (this.draftId === null) {
      return;
    }

    this.showDeleteConfirmDialog = false;

    try {
      await firstValueFrom(this.api.deleteDraft(this.draftId));
      console.log('✓ Draft deleted successfully');
      
      // Reset form and close dialog
      this.resetForm();
      this.close.emit();
    } catch (err) {
      console.error('Failed to delete draft:', err);
      const errorMsg = (err as any)?.error?.message || (err as any)?.message || 'Nepodarilo sa vymazať koncept.';
      this.showError(errorMsg);
    }
  }

  dismissDeleteConfirm(): void {
    this.showDeleteConfirmDialog = false;
  }

  onDeleteConfirmBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.dismissDeleteConfirm();
    }
  }

  /** Signed documents offered for signature removal (rebuilt when the dialog opens) */
  unsignOptions: UnsignOption[] = [];

  get unsignSelectedCount(): number {
    return this.unsignOptions.filter(o => o.selected).length;
  }

  get allUnsignSelected(): boolean {
    return this.unsignOptions.length > 0 && this.unsignOptions.every(o => o.selected);
  }

  get someUnsignSelected(): boolean {
    return this.unsignOptions.some(o => o.selected);
  }

  toggleUnsignAll(checked: boolean): void {
    for (const o of this.unsignOptions) {
      o.selected = checked;
    }
  }

  onRemoveSignature(): void {
    if (this.draftId === null || !this.anySigned) {
      return;
    }

    // Offer every currently signed document (all preselected = old behaviour)
    this.unsignOptions = [];
    if (this.formSigned) {
      this.unsignOptions.push({
        name: 'GeneralAgenda.xml',
        label: 'Formulár správy (GeneralAgenda.xml)',
        isForm: true,
        selected: true,
      });
    }
    for (const a of this.attachments) {
      if (a.signed) {
        this.unsignOptions.push({ name: a.file.name, label: a.file.name, isForm: false, selected: true });
      }
    }

    this.showRemoveSignatureDialog = true;
  }

  async confirmRemoveSignature(): Promise<void> {
    if (this.draftId === null || this.unsignSelectedCount === 0) {
      return;
    }

    const selected = this.unsignOptions.filter(o => o.selected);
    const removeAll = selected.length === this.unsignOptions.length;

    this.showRemoveSignatureDialog = false;

    try {
      // No documentNames = backend removes all signatures (backward-compatible)
      await firstValueFrom(this.api.removeSignature(
        this.draftId,
        removeAll ? undefined : selected.map(o => o.name)
      ));
      console.log('✓ Signature removed successfully', { removeAll, names: selected.map(o => o.name) });

      // Clear badges of the removed documents
      for (const o of selected) {
        if (o.isForm) {
          this.formSigned = false;
        } else {
          const att = this.attachments.find(a => a.file.name === o.name);
          if (att) {
            att.signed = false;
          }
        }
      }

      // The old job no longer matches the current signatures - drop it so a new
      // signing round can be prepared (backend replaces the job on the next sign).
      // Remaining signatures stay intact; only their badges (formSigned/att.signed) survive.
      this.signedSuccessfully.set(false);
      this.signingInProgress.set(false);
      this.pendingSignSelection = null;
      if (this.currentJobId) {
        this.signNotificationService.unsubscribeFromJob(this.currentJobId);
        this.currentJobId = null;
      }
      this.wsSubscription?.unsubscribe();
      this.deeplink = null;

      // Preselect only the documents without a signature for the next signing round
      this.signForm = !this.formSigned;
      for (const a of this.attachments) {
        a.sign = !a.signed;
      }

      // Save the draft to persist the unsigned state
      await this.ensureDraftSaved();
    } catch (err) {
      console.error('Failed to remove signature:', err);
      const errorMsg = (err as any)?.error?.message || (err as any)?.message || 'Nepodarilo sa odstrániť podpis.';
      this.showError(errorMsg);
    }
  }

  dismissRemoveSignatureConfirm(): void {
    this.showRemoveSignatureDialog = false;
  }

  onRemoveSignatureBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.dismissRemoveSignatureConfirm();
    }
  }

  submit(): void {
    this.touched = true;
    this.error.set(null);

    if (!this.recipientId || !this.subject.trim() || !this.body.trim()) {
      this.showError('Všetky povinné polia musia byť vyplnené.');
      return;
    }

    if (!this.draftId) {
      this.showError('Najprv musíte uložiť koncept.');
      return;
    }

    if (this.signingInProgress()) {
      this.showError('Podpisovanie stále prebieha. Prosím počkajte.');
      return;
    }

    if (this.currentJobId && !this.signedSuccessfully()) {
      this.confirmMessage.set('Dokument bol pripravený na podpis, ale nebol podpísaný. Chcete odoslať nepodpísaný dokument?');
      this.showConfirmDialog = true;
      return;
    }

    this.sendMessage();
  }

  private sendMessage(): void {
    this.sending.set(true);
    
    const request: SendGeneralAgendaRequest = {
      draftId: this.draftId!
    };

    this.api.sendGeneralAgenda(request).subscribe({
      next: (response) => {
        this.sending.set(false);
        console.log('✅ Message sent successfully:', response);
        
        this.sent.emit(request);
        
        // Show success dialog - it will close compose window when user dismisses it
        this.showSuccess('Správa bola úspešne odoslaná!', true);
      },
      error: (err) => {
        this.sending.set(false);
        console.error('❌ Failed to send message:', err);
        const errorMsg = err?.error?.message || err?.message || 'Nepodarilo sa odoslať správu.';
        this.showError(errorMsg);
      }
    });
  }

  private showError(message: string): void {
    this.error.set(this.cleanMessage(message));
    this.showErrorDialog = true;
  }

  confirmSendUnsigned(): void {
    this.showConfirmDialog = false;
    this.sendMessage();
  }

  cancelSendUnsigned(): void {
    this.showConfirmDialog = false;
    // User chose to continue signing, do nothing
  }

  dismissError(): void {
    this.showErrorDialog = false;
    this.error.set(null);
  }

  onErrorBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.dismissError();
    }
  }

  onConfirmBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.cancelSendUnsigned();
    }
  }

  showSuccessDialog = false;
  success = signal<string | null>(null);
  private shouldResetOnSuccessDismiss = false;
  showCancelConfirmDialog = false;
  showDeleteConfirmDialog = false;
  showRemoveSignatureDialog = false;

  private showSuccess(message: string, resetOnDismiss = false): void {
    this.success.set(this.cleanMessage(message));
    this.showSuccessDialog = true;
    this.shouldResetOnSuccessDismiss = resetOnDismiss;
  }

  private cleanMessage(message: string): string {
    if (!message) return message;
    
    // Remove common technical patterns from backend messages
    let cleaned = message
      // Remove IDs (e.g., "id: 123", "ID: abc", "jobId: xyz")
      .replace(/\b(id|job_?id|draft_?id|message_?id)\s*:?\s*[a-zA-Z0-9-_]+/gi, '')
      // Remove UUID patterns
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
      // Remove numeric IDs in parentheses like (ID: 12345)
      .replace(/\(\s*(id|job_?id|draft_?id)\s*:?\s*[0-9]+\s*\)/gi, '')
      // Remove standalone numbers that look like IDs
      .replace(/\bID:\s*\d+\b/gi, '')
      // Clean up extra whitespace and punctuation
      .replace(/\s+/g, ' ')
      .replace(/[,.]\s*[,.]/g, '.')
      .replace(/^[\s,.]+|[\s,.]+$/g, '')
      .trim();
    
    // Translate common English phrases to Slovak
    const translations: Record<string, string> = {
      // Success messages (most specific first)
      'signature job created successfully': 'Podpisová úloha bola úspešne vytvorená',
      'signature job created': 'Podpisová úloha bola vytvorená',
      'message sent successfully': 'Správa bola úspešne odoslaná',
      'message created successfully': 'Správa bola úspešne vytvorená',
      'draft saved successfully': 'Koncept bol úspešne uložený',
      'draft created successfully': 'Koncept bol úspešne vytvorený',
      'signing completed successfully': 'Podpisovanie bolo úspešne dokončené',
      'document signed successfully': 'Dokument bol úspešne podpísaný',
      'created successfully': 'vytvorené úspešne',
      'saved successfully': 'uložené úspešne',
      'sent successfully': 'odoslané úspešne',
      'completed successfully': 'dokončené úspešne',
      'signature completed': 'Podpis bol dokončený',
      'signing job completed': 'Podpisovanie bolo dokončené',
      'signing successful': 'Podpisovanie bolo úspešné',
      'document prepared for signing': 'Dokument je pripravený na podpísanie',
      'ready for signing': 'Pripravené na podpísanie',
      'prepared for signing': 'Pripravené na podpísanie',
      
      // Error messages
      'signing failed': 'Podpisovanie zlyhalo',
      'signature failed': 'Podpis zlyhal',
      'signing job failed': 'Podpisovanie zlyhalo',
      'failed to sign': 'Nepodarilo sa podpísať',
      'failed to send': 'Nepodarilo sa odoslať',
      'failed to save': 'Nepodarilo sa uložiť',
      'failed to create': 'Nepodarilo sa vytvoriť',
      'error occurred': 'Vyskytla sa chyba',
      'operation failed': 'Operácia zlyhala',
      'request failed': 'Požiadavka zlyhala',
      'invalid request': 'Neplatná požiadavka',
      'not found': 'Nenájdené',
      'unauthorized': 'Neautorizované',
      'forbidden': 'Zakázané',
      'timeout': 'Časový limit vypršal',
      
      // Status messages (only as whole words)
      'pending': 'čaká sa',
      'processing': 'spracováva sa',
      'completed': 'dokončené',
      'failed': 'zlyhalo'
    };
    
    // Individual word translations (applied after phrase translations)
    const wordTranslations: Record<string, string> = {
      'signature job': 'podpisová úloha',
      'signature': 'podpis',
      'message': 'správa',
      'draft': 'koncept',
      'document': 'dokument',
      'successfully': 'úspešne',
      'created': 'vytvorené',
      'saved': 'uložené',
      'sent': 'odoslané',
      'signed': 'podpísané',
      'prepared': 'pripravené',
      'ready': 'pripravené',
      'job': 'úloha',
      'signing': 'podpisovanie',
      'failed': 'zlyhalo',
      'error': 'chyba'
    };
    
    // Apply translations (case insensitive, whole phrases only)
    // Sort by length (longest first) to match most specific phrases first
    let translated = cleaned;
    const sortedTranslations = Object.entries(translations)
      .sort((a, b) => b[0].length - a[0].length);
    
    for (const [english, slovak] of sortedTranslations) {
      // Use word boundaries to match whole phrases only
      const regex = new RegExp('\\b' + english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
      translated = translated.replace(regex, slovak);
    }
    
    // Apply word translations for any remaining English words
    const sortedWords = Object.entries(wordTranslations)
      .sort((a, b) => b[0].length - a[0].length);
    
    for (const [english, slovak] of sortedWords) {
      const regex = new RegExp('\\b' + english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
      translated = translated.replace(regex, slovak);
    }
    
    return translated || message; // Return original if cleaning resulted in empty string
  }

  dismissSuccess(): void {
    this.showSuccessDialog = false;
    this.clearSuccess();
    
    // Only reset form if this was a "send" success, not a "prepare" success
    if (this.shouldResetOnSuccessDismiss) {
      this.handleSuccessClose();
    }
  }

  onSuccessBackdrop(ev: MouseEvent): void {
    if ((ev.target as HTMLElement)?.classList.contains('app-overlay')) {
      this.dismissSuccess();
    }
  }

  private clearSuccess(): void {
    this.success.set(null);
  }

  private handleSuccessClose(): void {
    if (this.autoCloseOnSuccess) {
      this.resetForm();
      this.close.emit();
    } else {
      this.resetForm();
    }
  }

  // --- FIXED METHODS (Added :void) ---
  onFormChange(): void {
    if (this.subject.trim()) {
      this.draftChangeSubject.next();
    }
  }

  onSubjectFocus(): void {
    // Intentionally empty
  }
  
  onSubjectClick(): void {
    // Intentionally empty
  }
  // ------------------------------------

  onFieldBlur(): void {
    if (this.subject.trim() && this.recipientId) {
      this.saveDraftImmediately();
    }
  }

  saveDraftImmediately(): void {
    if (this.subject.trim() && this.recipientId) {
      this.saveDraft();
    }
  }

  async ensureDraftSaved(): Promise<void> {
    if (this.pendingSavePromise) {
      await this.pendingSavePromise;
    }
    if (this.subject.trim() && this.recipientId && !this.savingDraft()) {
      await this.saveDraft();
    }
  }

  private saveDraft(): Promise<void> {
    if (this.savingDraft() || !this.subject.trim() || !this.recipientId) {
      return Promise.resolve();
    }
    this.savingDraft.set(true);
    const attachments: AttachmentDto[] = this.attachments
      .filter((a) => !!a.base64)
      .map((a) => ({
        unsignedDocumentName: a.file.name,
        unsignedMimetype: a.file.type || 'application/octet-stream',
        unsignedContent: a.base64 as string,
      }));

    const request: CreateDraftRequest = {
      draftId: this.draftId,
      subject: this.subject.trim(),
      content: this.body || '',
      recipientEdeskUri: this.recipientId,
      attachments: attachments,
    };

    this.pendingSavePromise = firstValueFrom(
      this.api.saveDraft(request)
    ).then(
      (response) => {
        this.savingDraft.set(false);
        this.pendingSavePromise = null;
        if (response.draftId) {
          this.draftId = response.draftId;
        }
      },
      (err) => {
        this.savingDraft.set(false);
        this.pendingSavePromise = null;
        throw err;
      }
    );
    return this.pendingSavePromise;
  }

async onPrepareSign(): Promise<void> {
  this.touched = true;
  this.error.set(null);

  // 1. Validation check
  if (!this.recipientId || !this.subject.trim() || !this.body.trim()) {
    return;
  }

  if (!this.hasSignSelection) {
    this.showError('Vyberte aspoň jeden dokument na podpísanie.');
    return;
  }

  try {
    // 2. Save draft
    await this.ensureDraftSaved();
  } catch (err) {
    this.showError('Nepodarilo sa uložiť koncept pred prípravou na podpis.');
    return;
  }

  const attachments: AttachmentDto[] = this.attachments
    .filter((a) => !!a.base64)
    .map((a) => ({
      unsignedDocumentName: a.file.name,
      unsignedMimetype: a.file.type || 'application/octet-stream',
      unsignedContent: a.base64 as string,
    }));

  const attachmentsToSign = this.attachments
    .filter((a) => !!a.base64 && a.sign)
    .map((a) => a.file.name);

  const request: SignAndSendRequest = {
    draftId: this.draftId,
    subject: this.subject.trim(),
    content: this.body || '',
    recipientEdeskUri: this.recipientId,
    attachments: attachments,
    signForm: this.signForm,
    attachmentsToSign: attachmentsToSign,
  };

  // Remember what was selected so the signed badges match this job on completion
  this.pendingSignSelection = { form: this.signForm, names: attachmentsToSign };

  this.sending.set(true);

  // 3. Call the API to prepare signing job
  this.api.signDraft(request).subscribe({
    next: (response: SignAndSendResponse) => {
      this.sending.set(false);

      if (response.status === 'ERROR') {
        this.showError(response.message || 'Nepodarilo sa pripraviť dokument na podpis.');
        return;
      }

      if (response.status === 'SIGNING') {
        // Store jobId and deeplink for the next step
        if (response.jobId) {
          this.currentJobId = response.jobId;
        }

        const link = response.deeplink
          ?? (response.jobId
            ? `qsignmobile://sign?job_id=${encodeURIComponent(response.jobId)}&callback=${encodeURIComponent(environment.apiBase + '/signature/done')}`
            : undefined);

        if (link) {
          this.deeplink = link;
          return;
        }
        return;
      }

      if (response.status === 'SENT') {
        this.showSuccess('Správa bola úspešne podpísaná a odoslaná.', true);
        return;
      }

      this.showSuccess('Koncept bol uložený.', false);
    },
    error: (err) => {
      this.sending.set(false);
      const backendMsg = err?.error?.message || err?.message || 'Nepodarilo sa pripraviť dokument na podpis.';
      this.showError(backendMsg);
    },
  });
}

  private setupVisibilityHandler(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible' && this.currentJobId && this.signingInProgress()) {
        console.log('👁️ Page became visible while signing in progress, checking job status...');
        // Wait a bit for WebSocket to potentially reconnect
        setTimeout(() => {
          if (this.signingInProgress() && this.currentJobId) {
            this.checkJobStatus(this.currentJobId);
          }
        }, 2000);
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private checkJobStatus(jobId: string): void {
    console.log('🔍 Checking job status for:', jobId);
    
    this.api.getSigningJobStatus(jobId).subscribe({
      next: (response) => {
        console.log('✅ Job status check result:', response);
        
        if (response.status === 'COMPLETED') {
          // Signing completed, update UI
          this.signingInProgress.set(false);
          this.signedSuccessfully.set(true);
          this.markSelectedAsSigned();
          this.deeplink = null;
          
          const message = response.message || 'Dokument bol úspešne podpísaný! Teraz môžete odoslať správu.';
          this.showSuccess(this.cleanMessage(message), false);
          
          // Clean up WebSocket subscription
          this.signNotificationService.unsubscribeFromJob(jobId);
          this.wsSubscription?.unsubscribe();
        } else if (response.status === 'FAILED') {
          // Signing failed, update UI
          this.signingInProgress.set(false);
          this.signedSuccessfully.set(false);
          this.pendingSignSelection = null;
          this.deeplink = null;
          this.currentJobId = null;
          
          const errorMsg = response.errorMessage || response.message || 'Podpisovanie zlyhalo. Prosím skúste znova.';
          this.showError(this.cleanMessage(errorMsg));
          
          // Clean up WebSocket subscription
          this.signNotificationService.unsubscribeFromJob(jobId);
          this.wsSubscription?.unsubscribe();
        }
        // If PENDING, keep waiting - WebSocket will handle it when it reconnects
      },
      error: (err) => {
        console.error('❌ Failed to check job status:', err);
        // Don't show error to user - WebSocket might still work
      }
    });
  }

  private listenForJobCompletion(jobId: string): void {
    console.log(`📡 Listening for updates on JobId: ${jobId}`);
    console.log(`🔍 WebSocket Connected: ${this.signNotificationService.isWebSocketConnected()}`);
    console.log(`📋 Current subscriptions: ${this.signNotificationService.getActiveSubscriptions().join(', ')}`);
    
    this.signNotificationService.subscribeToJob(jobId);
    
    this.wsSubscription = this.signNotificationService.jobUpdates$.subscribe((update: SignJobUpdate) => {
      console.log('🔔 Received WebSocket update:', update);
      
      // Only process updates for the current job
      if (update.jobId !== jobId) {
        return;
      }
      
      if (update.status === 'COMPLETED') {
        this.signingInProgress.set(false);
        this.signedSuccessfully.set(true);
        this.markSelectedAsSigned();
        this.deeplink = null;
        
        this.showSuccess('Dokument bol úspešne podpísaný! Teraz môžete odoslať správu.', false);
        
        // Clean up WebSocket subscription
        this.signNotificationService.unsubscribeFromJob(jobId);
        this.wsSubscription?.unsubscribe();
      } else if (update.status === 'FAILED') {
        this.signingInProgress.set(false);
        this.signedSuccessfully.set(false);
        this.pendingSignSelection = null;
        this.deeplink = null;
        this.currentJobId = null;
        
        const errorMsg = update.message ? this.cleanMessage(update.message) : 'Podpisovanie zlyhalo. Prosím skúste znova.';
        this.showError(errorMsg);
        
        // Clean up WebSocket subscription
        this.signNotificationService.unsubscribeFromJob(jobId);
        this.wsSubscription?.unsubscribe();
      } else if (update.status === 'PENDING') {
        console.log('⏳ Signing still in progress...');
        this.signingInProgress.set(true);
      }
    });
  }

  // Called when user clicks "Podpísať" button (after preparation)
  onSignClick(event: MouseEvent): void {
    event.preventDefault();
    
    if (!this.currentJobId) {
      console.error('❌ No jobId available');
      this.showError('Chýba identifikátor podpisovej úlohy.');
      return;
    }

    if (!this.deeplink) {
      console.error('❌ No deeplink available');
      this.showError('Chýba odkaz na podpisovú aplikáciu.');
      return;
    }

    console.log('🔐 User clicked "Podpísať" - Opening deeplink and starting WebSocket subscription...');
    
    // Start signing process
    this.signingInProgress.set(true);
    
    // Start listening for job completion
    this.listenForJobCompletion(this.currentJobId);
    
    // Use hidden anchor element to open deeplink (best method for iOS compatibility)
    // This approach worked best in testing, especially on Safari iOS
    if (this.deeplinkAnchor) {
      this.deeplinkAnchor.nativeElement.click();
    }
  }

  // Cancel the signing process (frontend only - stops waiting for completion)
  cancelSigning(): void {
    console.log('🚫 User cancelled signing process');
    
    // Stop WebSocket subscription
    if (this.currentJobId) {
      this.signNotificationService.unsubscribeFromJob(this.currentJobId);
    }
    this.wsSubscription?.unsubscribe();
    
    // Reset signing state
    this.signingInProgress.set(false);
    
    // Keep deeplink and jobId so user can try signing again if needed
    // If you want to fully reset, uncomment these:
    // this.deeplink = null;
    // this.currentJobId = null;
  }

  // ==================== TESTING UTILITIES ====================
  // These methods help test WebSocket functionality without a real backend callback

  testSigningSuccess(): void {
    if (!this.currentJobId) return;
    
    // If not already listening, start now (for testing purposes)
    if (!this.signingInProgress()) {
      console.log('🧪 Test: Starting WebSocket subscription first...');
      this.signingInProgress.set(true);
      this.listenForJobCompletion(this.currentJobId);
    }
    
    console.log('🧪 Testing: Simulating COMPLETED status');
    this.signNotificationService.simulateJobUpdate(
      this.currentJobId,
      'COMPLETED',
      'Test: Dokument bol úspešne podpísaný!'
    );
  }

  testSigningFailed(): void {
    if (!this.currentJobId) return;
    
    // If not already listening, start now (for testing purposes)
    if (!this.signingInProgress()) {
      console.log('🧪 Test: Starting WebSocket subscription first...');
      this.signingInProgress.set(true);
      this.listenForJobCompletion(this.currentJobId);
    }
    
    console.log('🧪 Testing: Simulating FAILED status');
    this.signNotificationService.simulateJobUpdate(
      this.currentJobId,
      'FAILED',
      'Test: Podpisovanie zlyhalo!'
    );
  }

  testSigningPending(): void {
    if (!this.currentJobId) return;
    
    // If not already listening, start now (for testing purposes)
    if (!this.signingInProgress()) {
      console.log('🧪 Test: Starting WebSocket subscription first...');
      this.signingInProgress.set(true);
      this.listenForJobCompletion(this.currentJobId);
    }
    
    console.log('🧪 Testing: Simulating PENDING status');
    this.signNotificationService.simulateJobUpdate(
      this.currentJobId,
      'PENDING',
      'Test: Podpis prebieha...'
    );
  }
  // ============================================================
}